import { CarouselButton } from '@carvana/ds-button';
import createComponent from '@carvana/merch-components/createComponent';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { useState, useRef, useCallback, useEffect } from 'react';
import Slider from 'react-slick';
import type { Settings as SlickSettings } from 'react-slick';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import {
  ShaqvanaYoutubeIds,
  useConfiguration
} from '~/context/common/StaticContext';
import optimizeImage, {
  type Options as OptimizeOptions
} from '~/utilities/optimizeImage';
import VideoModal from './VideoModal';
import VideoPoster, { type VideoPosterProps } from './VideoPoster';

const commonOptions: OptimizeOptions = {
  format: 'auto',
  optimize: 'high',
  quality: 90,
  width: 415
};

// Define video metadata with experience values
type VideoMetadata = VideoPosterProps & {
  experienceValue: string;
};

const createVideos = (
  shaqvanaYoutubeIds: ShaqvanaYoutubeIds = {}
): VideoMetadata[] => [
  {
    id: 'shaqmeetshaqbot',
    youtubeId: shaqvanaYoutubeIds.shaqmeetshaqbot || null,
    image: optimizeImage(
      `${appAssetsCdnUrl}/images/shaqvana/shaqvana-videocarousel-meetshaqbot.jpg`,
      commonOptions
    ),
    description: 'Watch me test the world’s first Shaq-powered A.I.',
    experienceValue: 'meetshaqbot'
  },
  {
    id: 'shaqparkinglot',
    youtubeId: shaqvanaYoutubeIds.shaqparkinglot || null,
    image: optimizeImage(
      `${appAssetsCdnUrl}/images/shaqvana/shaqvana-videocarousel-parkinglot-1.jpg`,
      commonOptions
    ),
    description: 'Watch my TV commercial "Intro to Shaqvana"',
    experienceValue: 'welcome'
  },
  {
    id: 'shaqsminicooper',
    youtubeId: shaqvanaYoutubeIds.shaqsminicooper || null,
    image: optimizeImage(
      `${appAssetsCdnUrl}/images/shaqvana/shaqvana-videocarousel-mini-2.jpg`,
      commonOptions
    ),
    description: 'Good thing we offer a 7-Day Money Back Guarantee',
    experienceValue: 'moneybackguarantee'
  },
  {
    id: 'shaqremodeling',
    youtubeId: shaqvanaYoutubeIds.shaqremodeling || null,
    image: optimizeImage(
      `${appAssetsCdnUrl}/images/shaqvana/shaqvana-videocarousel-sign-3.jpg`,
      commonOptions
    ),
    description: 'Time for a little Shaqvana remodeling',
    experienceValue: 'shaqvanaishere'
  }
];

type SlideContentProps = {
  isCurrentSlide: boolean;
  children: React.ReactNode;
};

const SlideContent = createComponent<SlideContentProps>(
  'div',
  ({ isCurrentSlide }) => ({
    className: cx(
      'transition-scale z-10 flex flex-col items-start justify-end duration-700 hover:cursor-pointer',
      !isCurrentSlide ? 'scale-[0.85]' : ''
    )
  })
);

export default function VideosCarousel() {
  const trackEvent = useTrackEvent();
  const { shaqvanaYoutubeIds } = useConfiguration();
  const router = useRouter();
  const VIDEOS = createVideos(shaqvanaYoutubeIds);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const sliderRef = useRef<Slider>(null);
  const currentSlideRef = useRef(0);
  const isDraggingRef = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const isAutoOpeningRef = useRef(false);

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  // Check for experience parameter on page load and auto-show the modal
  useEffectOnce(() => {
    setIsMounted(true);

    // Create a map of experience values to video indices
    const experienceToIndexMap: Record<string, number> = {};
    VIDEOS.forEach((video, index) => {
      experienceToIndexMap[video.experienceValue] = index;
    });

    const { exp: experience } = router.query;

    if (experience && typeof experience === 'string') {
      const videoIndex = experienceToIndexMap[experience];

      if (videoIndex !== undefined) {
        isAutoOpeningRef.current = true;

        // Set the current video
        setCurrentSlide(videoIndex);

        // Scroll to the specific slide to ensure it's centered
        sliderRef.current?.slickGoTo(videoIndex);

        // Open the video modal immediately
        setIsVideoModalOpen(true);

        // Track the event
        trackEvent('Video Carousel', {
          action: 'auto-shown',
          description: 'Modal automatically opened from experience parameter',
          text: VIDEOS[videoIndex]?.description || '',
          youtubeId: VIDEOS[videoIndex]?.youtubeId || '',
          experience
        });

        // Reset auto-opening flag after transition completes (700ms speed)
        setTimeout(() => {
          isAutoOpeningRef.current = false;
        }, 800);
      }
    }
  }, [VIDEOS, router.query, trackEvent]);

  const currentVideo = VIDEOS[currentSlide] || VIDEOS[0];

  const handleBeforeChange = (_current: number, next: number) => {
    setCurrentSlide(next);

    if (isVideoModalOpen && !isAutoOpeningRef.current) {
      setIsVideoModalOpen(false);
    }
  };

  const handleAfterChange = (index: number) => {
    trackEvent('Video Carousel', {
      action: 'swiped',
      description: 'Slide changed',
      text: VIDEOS[index]?.description || '',
      index
    });
  };

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  const handleOpenVideo = useCallback(() => {
    const hasYoutubeId = Boolean(currentVideo?.youtubeId);
    // Don't open Modal if no youtubeId
    if (!hasYoutubeId) return;

    setIsVideoModalOpen(true);
    trackEvent('Video Carousel', {
      action: 'clicked',
      description: 'Open video modal',
      text: currentVideo?.description || '',
      youtubeId: currentVideo?.youtubeId || ''
    });
  }, [currentVideo, trackEvent]);

  const handleCloseVideo = useCallback(() => {
    setIsVideoModalOpen(false);
  }, [setIsVideoModalOpen]);

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    isDraggingRef.current = false;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartPos.current = { x: clientX, y: clientY };
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const diffX = Math.abs(clientX - dragStartPos.current.x);
    const diffY = Math.abs(clientY - dragStartPos.current.y);

    // If moved more than 5px, consider it a drag
    if (diffX > 5 || diffY > 5) {
      isDraggingRef.current = true;
    }
  };

  const handleSlideClick = useCallback(
    (index: number) => {
      // Don't process click if user was dragging
      if (isDraggingRef.current) {
        return;
      }

      if (index === currentSlideRef.current) {
        handleOpenVideo();

        return;
      }

      const current = currentSlideRef.current;
      const totalSlides = VIDEOS.length;

      // Calculate if this is the next or previous slide (accounting for infinite wrap)
      const isNextSlide = index === (current + 1) % totalSlides;
      const isPrevSlide = index === (current - 1 + totalSlides) % totalSlides;

      if (isNextSlide) {
        sliderRef.current?.slickNext();
      } else if (isPrevSlide) {
        sliderRef.current?.slickPrev();
      } else {
        sliderRef.current?.slickGoTo(index);
      }
    },
    [handleOpenVideo, VIDEOS.length]
  );

  const sliderSettings: SlickSettings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '0px',
    dots: false,
    arrows: false,
    speed: 700,
    swipeToSlide: true,
    focusOnSelect: false,
    beforeChange: handleBeforeChange,
    afterChange: handleAfterChange,
    draggable: true,
    touchThreshold: 10
  };

  return (
    <div className="min-h-[310px] w-[790px] sm:min-h-[435px] sm:w-[1272px] md:min-h-[465px]">
      <div className="relative flex w-full flex-col items-center">
        <div className="gap-6 flex w-full flex-col items-center">
          <Slider ref={sliderRef} {...sliderSettings} className="w-full">
            {VIDEOS.map((video, index) => {
              const isCurrentSlide = isMounted && index === currentSlide;

              return (
                <div
                  key={video.id}
                  className="px-[0.4rem]"
                  data-analytics-attributes={JSON.stringify({
                    description: video.description,
                    index
                  })}
                  data-analytics-id="Video Carousel Slide"
                  data-testid="video-slide"
                  onClick={() => handleSlideClick(index)}
                  onKeyDown={ev => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      handleSlideClick(index);
                    }
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onTouchStart={handleMouseDown}
                  onTouchMove={handleMouseMove}
                  role="button"
                  tabIndex={0}
                >
                  <SlideContent isCurrentSlide={isCurrentSlide}>
                    <VideoPoster isActive={isCurrentSlide} {...video} />
                  </SlideContent>
                </div>
              );
            })}
          </Slider>
        </div>

        <CarouselButton
          aria-label="Previous slide"
          className="absolute left-[10px] top-[165px] flex items-center text-icon-primary"
          data-analytics-attributes={JSON.stringify({
            description: 'prev'
          })}
          data-analytics-id="Video Carousel"
          data-testid="carousel-prev"
          direction="left"
          onClick={goToPrev}
        />
        <CarouselButton
          aria-label="Next slide"
          className="absolute right-[10px] top-[165px] flex items-center text-icon-primary"
          data-analytics-attributes={JSON.stringify({
            description: 'next'
          })}
          data-analytics-id="Video Carousel"
          data-testid="carousel-next"
          direction="right"
          onClick={goToNext}
        />
      </div>

      <VideoModal
        isOpen={isVideoModalOpen}
        description={currentVideo?.description}
        onClose={handleCloseVideo}
        videoUrl={`https://www.youtube.com/embed/${currentVideo?.youtubeId}`}
      />
    </div>
  );
}
